/**
 * Cards you can put a finger on.
 *
 * ## The hand: pick up, and throw
 *
 * Any card in your hand can be picked up and moved about; it follows the finger,
 * leaning into the way it is being moved, and springs back into the fan when it
 * is let go. A card you are allowed to play can also be *thrown*, and it is
 * played exactly as a tap would play it, when it is let go either
 *
 *   - onto your own place in the trick — carried close to it, at any speed
 *     (within `NEAR_ACROSS` × `NEAR_ALONG` of a card of its centre); or
 *   - flicked — moving upwards when the finger lifts, even gently and even a
 *     short way, as long as it was not being pulled back down at the end.
 *
 * The flick is judged on the fastest moment of the last tenth of a second, not
 * on an average: a real thumb eases off as it lets go, and an average over the
 * whole release is what made a quick, short flick spring back.
 *
 * Nothing new is needed to get it from there to the table. The drag is written
 * with the individual `translate` and `rotate` properties on `.card-lift`, which
 * compose outside the lift the state classes give it, and `noteOrigin`
 * (flight.ts) reads the lift's box as the card leaves the hand — so the flight
 * to the trick starts from wherever the finger let go, at the angle it was let
 * go at. A tap is still a tap: nothing moves until the finger has gone further
 * than `SLOP`, and a drag swallows the click that would otherwise follow it.
 *
 * A throw is held where it was let go until the server answers. If the answer
 * takes the card, it flies from there; if it does not (the Joker has to name a
 * suit first, or the play was refused), the card springs back into the fan.
 *
 * ## The felt: nudge
 *
 * The cards in the trick can be pushed about a little — against a rubber band,
 * so they never go far — and settle back where they were thrown when let go.
 * It changes nothing and tells nobody; it is the thing your hand does to a card
 * on a real table while you wait for someone to play.
 *
 * Both are the `flick` setting. Reduced motion keeps the throw (it is a way to
 * play, not a flourish) but drops the nudge.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'
import { buzz, BUZZ_PICK, BUZZ_THROW } from '../settings/haptics'
import { prefersReducedMotion } from '../settings/settings'
import { shareGesture } from './gestures'
import { measureFrame, toTable } from './tableFrame'
import { probe } from '../debug/probe'

/** How far a finger moves before a press becomes a drag, in px. Under it, the
 *  press is a tap and is left alone. */
const SLOP = 8
/** A flick: at its fastest in the last `VELOCITY_MS`, moving upwards at least
 *  this fast (px/ms), and at least `FLICK_MIN_RISE` px above where it was
 *  picked up. Measured on an iPhone: a real flick leaves the thumb at 3–4 px/ms,
 *  while carrying a card gently towards the table — which must not light the
 *  drop cue — stays well under 1. 0.25 lit it for a gentle carry. */
const FLICK_SPEED = 1.2
const FLICK_MIN_RISE = 24
/** …and still rising at least this fast as the finger lifts, and mostly
 *  upwards rather than sideways. A thumb eases off as it lets go but does not
 *  stop; a card being waved about in the hand and dropped does. */
const STILL_RISING = 0.4
/** The finger counts as still, for the lean, after this long without moving. */
const STILL_MS = 60
/** Per-frame easing of the lean towards where the finger's motion puts it. */
const LEAN_FOLLOW = 0.3
/** A thrown card carries its release velocity this long before it turns
 *  onto your place, and no further than `CARRY_MAX` px. */
const CARRY_MS = 90
const CARRY_MAX = 110
/** A finger that has not moved for this long before lifting had stopped. */
const PAUSED_MS = 70
/** The window velocity is measured over, in ms. */
const VELOCITY_MS = 150
/** A speed is measured over at least this long, so two samples a millisecond
 *  apart cannot make a twitch look like a flick. */
const SPEED_SPAN_MS = 16
/** A throw that nothing took up — no re-render came — springs back after this. */
const HOLD_MS = 450

export interface Sample {
  readonly x: number
  readonly y: number
  readonly t: number
}

export interface Throwable {
  /** Present when letting the card go upwards should play it. */
  readonly onThrow?: () => void
  /** The card is on its way to the table (the server has not answered). */
  readonly launching?: boolean
}

/**
 * Pick-up and throw for a fan. Cards are found by `data-card` on `.card`, so
 * one set of listeners on the fan serves every card, and a re-render never
 * re-binds anything.
 */
export function useHandFlick(
  host: RefObject<HTMLElement | null>,
  enabled: boolean,
  lookup: (key: string) => Throwable | undefined,
): void {
  const lookupRef = useRef(lookup)
  lookupRef.current = lookup
  /** Thrown and waiting on the server, by card key. */
  const held = useRef(new Map<string, HTMLElement>())
  /** Puts back the classes the drag wrote on the card it is carrying. */
  const remark = useRef<() => void>(() => {})

  useEffect(() => {
    const el = host.current
    if (!el || !enabled) return
    let drag: {
      id: number
      key: string
      card: HTMLElement
      lift: HTMLElement
      x0: number
      y0: number
      dx: number
      dy: number
      moving: boolean
      /** Letting go now plays it: the drop cue is lit. */
      armed: boolean
      /** Close enough to your place in the trick to be dropped on it. */
      near: boolean
      lean: number
      samples: Sample[]
      /** When the finger last moved (event time). */
      lastT: number
      /** Measured once, as the card is picked up (see `Geometry`). */
      g: Geometry | null
    } | null = null
    let swallowClick = false
    let recheck = 0
    let frame = 0
    remark.current = () => {
      if (!drag?.moving) return
      drag.card.classList.add('dragging')
      drag.card.classList.toggle('armed', drag.armed)
    }

    /** Forget a drag whose card has left the page — the hand ended, or the
     *  card was played from elsewhere, under the finger. Its lift never hears
     *  the finger let go (iOS keeps sending a removed element's pointer events
     *  to the removed element, where nothing here listens), and a drag nobody
     *  ends is one that blocks every pick-up after it. */
    const abandon = () => {
      const d = drag
      if (!d || d.card.isConnected) return
      drag = null
      window.clearTimeout(recheck)
      if (frame) cancelAnimationFrame(frame)
      frame = 0
      d.card.classList.remove('dragging', 'armed')
      showDrop(null)
      if (d.moving && d.g) shareHandAt('end', d.g, centreOf(d.g, d.dx, d.dy, d.lean), d.dy, d.lean)
      probe('drag', 'abandoned')
    }

    const down = (e: PointerEvent) => {
      abandon()
      if (drag || (e.pointerType === 'mouse' && e.button !== 0)) return
      // The deal is still turning the cards over: nothing is in anyone's hand yet.
      if (el.classList.contains('dealing')) return
      const card = (e.target as Element | null)?.closest<HTMLElement>('.card')
      const key = card?.dataset.card
      const lift = card?.querySelector<HTMLElement>('.card-lift')
      probe('down', { card: key ?? null, onCard: !!card, held: key ? held.current.has(key) : null, throwable: key ? !!lookupRef.current(key)?.onThrow : null })
      if (!card || !key || !lift || !el.contains(card) || held.current.has(key)) return
      drag = { id: e.pointerId, key, card, lift, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, moving: false, armed: false, near: false, lean: 0, samples: [sample(e)], lastT: e.timeStamp, g: null }
    }

    const move = (e: PointerEvent) => {
      abandon()
      if (!drag || e.pointerId !== drag.id) return
      drag.dx = e.clientX - drag.x0
      drag.dy = e.clientY - drag.y0
      if (!drag.moving) {
        if (Math.hypot(drag.dx, drag.dy) < SLOP) return
        drag.moving = true
        try {
          drag.card.setPointerCapture(e.pointerId)
        } catch {
          /* the pointer is already gone */
        }
        // Measured after `dragging` holds the lift steady (app.css): the press
        // dip under a finger is mid-transition at this moment, and a centre
        // taken from it is a few pixels off for the whole drag.
        drag.card.classList.add('dragging')
        drag.g = measureDrag(drag.lift)
        probe('drag', 'started')
        buzz(BUZZ_PICK)
      }
      drag.samples.push(sample(e))
      trim(drag.samples)
      drag.lastT = e.timeStamp
      // A finger that stops is no longer flicking: look again once it has
      // been still long enough for a release to count as setting it down.
      window.clearTimeout(recheck)
      const d = drag
      recheck = window.setTimeout(() => {
        if (drag === d && lookupRef.current(d.key)?.onThrow) arm(d, d.near)
      }, PAUSED_MS)
      // Drawn once a frame, however many moves arrive in it: a phone reports
      // the finger at 120 Hz or more, and every one of those used to write the
      // card's place and then read the page back, which is a layout per move.
      if (!frame) frame = requestAnimationFrame(paint)
    }

    /** Put the carried card where the finger is, this frame. Reads nothing
     *  from the page: everything it needs was measured at pick-up. */
    const paint = () => {
      frame = 0
      const d = drag
      if (!d || !d.moving || !d.g) return
      const still = performance.now() - d.lastT > STILL_MS
      const v = still ? { x: 0, y: 0 } : velocity(d.samples)
      // It leans into the way it is being carried, the way a card held at one
      // corner does — eased, so the finger's jitter never reaches it — and
      // straightens as the finger slows.
      const lean = clamp(d.dx * 0.07 + v.x * 9, -24, 24)
      d.lean += (lean - d.lean) * LEAN_FOLLOW
      // The card sits turned in the fan's arc, and the drag is written inside
      // that turn: undo it, or a card carried the height of the screen slides
      // several pixels off the fingertip on the way.
      const [lx, ly] = unturn(d.g.fan, d.dx, d.dy)
      d.lift.style.translate = `${lx.toFixed(1)}px ${ly.toFixed(1)}px`
      d.lift.style.rotate = `${d.lean.toFixed(2)}deg`
      const at = centreOf(d.g, d.dx, d.dy, d.lean)
      shareHandAt('move', d.g, at, d.dy, d.lean)
      // Show where a playable card would land, and whether letting go now
      // would put it there — the very state the release acts on, so the cue
      // can never say one thing and the table do another.
      if (lookupRef.current(d.key)?.onThrow) {
        d.near = d.g.slot ? isNear(at, d.g.slot, d.near) : false
        const flick = isFlick(d.dy, d.samples, d.lastT)
        arm(d, d.near || flick)
        const r = rise(d.samples)
        probe('move', {
          dy: Math.round(d.dy),
          near: d.near,
          flick,
          fastestUp: +r.fastestUp.toFixed(2),
          nowUp: +r.atRelease.toFixed(2),
        })
      }
      if (Math.abs(lean - d.lean) > 0.2) frame = requestAnimationFrame(paint)
    }

    /** Light the drop cue, or dim it: `armed` is "letting go now plays it". */
    const arm = (d: NonNullable<typeof drag>, armed: boolean) => {
      if (armed !== d.armed) {
        d.armed = armed
        d.card.classList.toggle('armed', armed)
        if (armed) buzz(BUZZ_PICK)
      }
      showDrop(armed ? 'armed' : 'ready')
    }

    const up = (e: PointerEvent) => {
      abandon()
      if (!drag || e.pointerId !== drag.id) return
      // The last move may not have been drawn yet: draw it, so the decision
      // is made on where the card really is.
      if (frame) {
        cancelAnimationFrame(frame)
        paint()
      }
      const d = drag
      drag = null
      if (!d.moving) return
      swallowClick = true
      window.setTimeout(() => (swallowClick = false), 400)
      d.card.classList.remove('dragging', 'armed')
      window.clearTimeout(recheck)
      showDrop(null)
      // Played exactly when the drop cue says so. The cue was set from the
      // last move — near your place, or mid-flick — and dimmed if the finger
      // then stood still (`recheck`), so what the player saw lit is what
      // happens, to the millisecond. The lift itself is not a sample: a
      // finger leaves the glass where it last moved, a moment later, and
      // counting that as the card standing still once cancelled every flick.
      const entry = lookupRef.current(d.key)
      probe('release', { type: e.type, dy: Math.round(d.dy), armed: d.armed, near: d.near, throwable: !!entry?.onThrow })
      const thrown = e.type === 'pointerup' && entry?.onThrow !== undefined && d.armed
      const at = d.g ? centreOf(d.g, d.dx, d.dy, d.lean) : null
      if (!thrown) {
        if (d.g && at) shareHandAt('end', d.g, at, d.dy, d.lean)
        settle(d.lift)
        return
      }
      if (d.g && at) shareHandAt('throw', d.g, at, d.dy, d.lean)
      // It keeps going. A card let go does not stop dead in the air while the
      // server is asked: it carries on the way it was thrown and curves down
      // onto your place, and the flight into the trick takes it from wherever
      // it has got to when the answer comes.
      carryOn(d.lift, d, velocity(d.samples), performance.now() - d.lastT > STILL_MS)
      buzz(BUZZ_THROW)
      held.current.set(d.key, d.lift)
      entry.onThrow?.()
      window.setTimeout(release, HOLD_MS)
    }

    const click = (e: MouseEvent) => {
      if (!swallowClick) return
      swallowClick = false
      e.stopPropagation()
      e.preventDefault()
    }

    el.addEventListener('pointerdown', down)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    el.addEventListener('click', click, true)
    // A captured card that is removed says so at the document.
    document.addEventListener('lostpointercapture', abandon)
    return () => {
      remark.current = () => {}
      el.removeEventListener('pointerdown', down)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      el.removeEventListener('click', click, true)
      document.removeEventListener('lostpointercapture', abandon)
      if (drag) {
        drag.card.classList.remove('dragging', 'armed')
        settle(drag.lift)
      }
      if (frame) cancelAnimationFrame(frame)
      window.clearTimeout(recheck)
      showDrop(null)
    }
  }, [enabled])

  /** Let go of any throw that was not taken up. A card that left the hand is
   *  simply forgotten — its flight has already read where it was. */
  function release(): void {
    for (const [key, lift] of held.current) {
      const entry = lookupRef.current(key)
      if (!entry || !lift.isConnected) {
        held.current.delete(key)
        continue
      }
      // Still on its way: the server has not answered yet, and the card waits
      // where it was let go for as long as the table does.
      if (entry.launching) continue
      settle(lift)
      held.current.delete(key)
    }
  }

  // After every render, before it is painted: `dragging`, `armed` and `thrown`
  // are written here and not by React, and React writes a card's whole
  // `className` whenever its state changes — which a throw always does, since
  // it marks the card `launch`. Without this the card being carried or thrown
  // drops under the felt and takes the launch step's extra rise mid-flight.
  useLayoutEffect(() => {
    remark.current()
    for (const lift of held.current.values()) {
      if (lift.isConnected) lift.parentElement?.classList.add('thrown')
    }
  })

  // After every render: a throw that opened a question instead of playing (the
  // Joker's suit), or that the server turned down, goes back into the hand.
  useEffect(() => {
    if (held.current.size > 0) release()
  })
}

/**
 * Nudge the cards in a trick. Listeners on the trick box; the cards opt in to
 * being touched in CSS (`pointer-events`), so the felt stays see-through to taps
 * everywhere else.
 */
export function useNudge(host: RefObject<HTMLElement | null>, enabled: boolean): void {
  useEffect(() => {
    const el = host.current
    if (!el || !enabled) return
    let drag: { id: number; card: HTMLElement; x0: number; y0: number } | null = null

    /** The trick was swept away under the finger: that nudge is over, and the
     *  next card touched is a new one (see `useHandFlick`'s `abandon`). */
    const abandon = () => {
      if (!drag || drag.card.isConnected) return
      const seat = Number(drag.card.dataset.seat)
      drag = null
      // Where it was is gone with it: the others only need to hear it let go.
      if (Number.isInteger(seat)) shareGesture({ phase: 'end', target: { kind: 'trick', seat }, x: 0, y: 0, lean: 0 })
    }

    const down = (e: PointerEvent) => {
      abandon()
      if (drag || prefersReducedMotion() || (e.pointerType === 'mouse' && e.button !== 0)) return
      const card = (e.target as Element | null)?.closest<HTMLElement>('.tcard')
      // Not while the trick is being swept off the table.
      if (!card || el.classList.contains('gather')) return
      drag = { id: e.pointerId, card, x0: e.clientX, y0: e.clientY }
      try {
        card.setPointerCapture(e.pointerId)
      } catch {
        /* gone already */
      }
      card.classList.add('nudged')
      buzz(BUZZ_PICK)
    }
    const move = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return
      // A rubber band: easy to start, harder the further it goes, and never
      // more than a card's width from where it was thrown.
      const dx = band(e.clientX - drag.x0)
      const dy = band(e.clientY - drag.y0)
      drag.card.style.translate = `${dx.toFixed(1)}px ${dy.toFixed(1)}px`
      drag.card.style.rotate = `${(dx * 0.12).toFixed(2)}deg`
      shareNudge(drag.card, 'move', dx * 0.12)
    }
    const up = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.id) return
      drag.card.classList.remove('nudged')
      shareNudge(drag.card, 'end', 0)
      drag.card.style.translate = ''
      drag.card.style.rotate = ''
      drag = null
    }
    el.addEventListener('pointerdown', down)
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
    document.addEventListener('lostpointercapture', abandon)
    return () => {
      el.removeEventListener('pointerdown', down)
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', up)
      el.removeEventListener('pointercancel', up)
      document.removeEventListener('lostpointercapture', abandon)
    }
  }, [enabled])
}

/** Where `x` stops being hidden: at the row of your own slot (`y` = 1) it is
 *  where the card really is, and from here down into the hand it is the
 *  middle of the fan. Between the two it fades, so the card is seen to come
 *  up out of the hand and then go where it is aimed. */
const HIDDEN_FROM_Y = 1.6

/** A card from your hand, where it is on the table (tableFrame.ts).
 *
 *  While it is still down among the hand, where it really is would give it
 *  away: across the fan is its suit (the hand is sorted), and how high it
 *  started is whether it is playable (a legal card rests lifted) and where on
 *  the fan's arc it sat. So down there it is reported as the middle of the
 *  hand, moved by as far as the finger has moved, and only as it comes up over
 *  the table does that give way to exactly where it is. */
function shareHandAt(phase: 'move' | 'end' | 'throw', g: Geometry, at: { x: number; y: number }, dy: number, lean: number): void {
  const f = g.frame
  if (!f || g.handY === null) return
  const real = toTable(at.x, at.y, f, g.handY - f.cy)
  const held = toTable(f.cx, g.handY + dy, f, g.handY - f.cy)
  const hide = Math.min(1, Math.max(0, (real.y - 1) / (HIDDEN_FROM_Y - 1)))
  shareGesture({
    phase,
    target: { kind: 'hand' },
    x: real.x * (1 - hide),
    y: real.y * (1 - hide) + held.y * hide,
    lean,
  })
}

/** A card in the trick, where it is on the table now. It is face up already:
 *  there is nothing about it to keep back. */
function shareNudge(card: HTMLElement, phase: 'move' | 'end', lean: number): void {
  const f = measureFrame()
  const seat = Number(card.dataset.seat)
  if (!f || !Number.isInteger(seat)) return
  const r = card.getBoundingClientRect()
  const at = toTable(r.left + r.width / 2, r.top + r.height / 2, f, f.sy * 2)
  shareGesture({ phase, target: { kind: 'trick', seat }, x: at.x, y: at.y, lean })
}

/** Back into place: clearing the drag lets the CSS transition carry it home. */
function settle(lift: HTMLElement): void {
  // A throw that was carrying on is stopped where it is, and springs home
  // from there rather than from where the finger let go.
  for (const a of lift.getAnimations?.() ?? []) {
    try {
      a.commitStyles()
    } catch {
      /* not rendered: nothing to keep */
    }
    a.cancel()
  }
  lift.parentElement?.classList.remove('thrown')
  // Let the committed place land before it is cleared, or the spring home
  // starts from where the finger was.
  void lift.offsetWidth
  lift.style.translate = ''
  lift.style.rotate = ''
}

function band(d: number): number {
  const limit = 46
  return (d / (1 + Math.abs(d) / limit)) * 1.1
}

function sample(e: PointerEvent): Sample {
  return { x: e.clientX, y: e.clientY, t: e.timeStamp }
}

/** How close to your place in the trick a card's centre has to be for letting
 *  go to drop it there, in the place's own widths and heights from its centre.
 *  Close enough that the card you are holding overlaps it by about a quarter
 *  or more: anywhere further out on the felt is not "on" it. */
const NEAR_ACROSS = 0.75
const NEAR_ALONG = 0.7
/** Once lit, the cue holds until the card is this much further out again, so
 *  it does not flicker on and off along the edge. */
const NEAR_HOLD = 1.2

/** Everything a drag needs from the page, read once as the card is picked
 *  up so that following the finger reads nothing: the card's centre before it
 *  moved, your place in the trick, and the table's frame for sharing it. */
interface Geometry {
  /** The card's centre in the hand, before the lift raised it. */
  readonly cx: number
  readonly cy: number
  /** How far the lift has raised it (negative is up), in the card's own frame, px. */
  readonly raise: number
  /** The card's angle in the fan's arc, degrees. */
  readonly fan: number
  readonly slot: { x: number; y: number; w: number; h: number } | null
  readonly frame: ReturnType<typeof measureFrame>
  readonly handY: number | null
}

function measureDrag(lift: HTMLElement): Geometry {
  const r = lift.getBoundingClientRect()
  const card = lift.parentElement ?? lift
  const box = card.getBoundingClientRect()
  const fan = parseFloat(/rotate\(([-\d.]+)deg\)/.exec(card.style.transform)?.[1] ?? '0') || 0
  const lifted = r.top + r.height / 2 - (box.top + box.height / 2)
  const raise = lifted / Math.max(Math.cos((fan * Math.PI) / 180), 0.5)
  const slot = document.querySelector<HTMLElement>('.play-felt .trick .drop-slot')
  const s = slot?.getBoundingClientRect()
  const hand = document.querySelector<HTMLElement>('.hand-band .hand')?.getBoundingClientRect()
  return {
    cx: box.left + box.width / 2,
    cy: box.top + box.height / 2,
    raise,
    fan,
    // Its own size, not the lit box's, which is drawn a little larger.
    slot: slot && s ? { x: s.left + s.width / 2, y: s.top + s.height / 2, w: slot.offsetWidth, h: slot.offsetHeight } : null,
    frame: measureFrame(),
    handY: hand ? hand.top + hand.height / 2 : null,
  }
}

/** Where the carried card's centre is. The individual `translate` and `rotate`
 *  the drag writes are applied *outside* the lift's own `transform`, so the
 *  lift's rise is turned by the lean: a card raised 14 px and leaning 30° sits
 *  7 px to one side of where adding up the finger's movement would put it. */
function centreOf(g: Geometry, dx: number, dy: number, lean: number): { x: number; y: number } {
  const a = ((g.fan + lean) * Math.PI) / 180
  return { x: g.cx + dx - g.raise * Math.sin(a), y: g.cy + dy + g.raise * Math.cos(a) }
}

/** A movement on screen, as the card — turned `deg` in the fan — has to be
 *  told it. */
function unturn(deg: number, x: number, y: number): [number, number] {
  const a = (-deg * Math.PI) / 180
  return [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)]
}

/** A thrown card carries on: along the way it was going when the finger let
 *  go, curving down onto your place, and slowing as it arrives. The curve's
 *  first leg is the release velocity carried for `CARRY_MS`, so the card
 *  leaves the finger at the speed and in the direction it was thrown. */
function carryOn(
  lift: HTMLElement,
  d: { dx: number; dy: number; lean: number; g: Geometry | null },
  v: { x: number; y: number },
  still: boolean,
): void {
  lift.parentElement?.classList.add('thrown')
  if (!d.g?.slot || prefersReducedMotion() || typeof lift.animate !== 'function') return
  const g = d.g
  const from = { x: d.dx, y: d.dy }
  // Lands with its lean gone, so its raise is back to the fan's own angle.
  const f = (g.fan * Math.PI) / 180
  const to = { x: g.slot!.x - g.cx + g.raise * Math.sin(f), y: g.slot!.y - g.cy - g.raise * Math.cos(f) }
  const speed = still ? 0 : Math.hypot(v.x, v.y)
  let kx = still ? 0 : v.x * CARRY_MS
  let ky = still ? 0 : v.y * CARRY_MS
  const reach = Math.hypot(kx, ky)
  if (reach > CARRY_MAX) {
    kx *= CARRY_MAX / reach
    ky *= CARRY_MAX / reach
  }
  const ctrl = { x: from.x + kx, y: from.y + ky }
  const dist = Math.hypot(to.x - from.x, to.y - from.y)
  const duration = clamp((dist / Math.max(speed, 0.9)) * 1.4, 180, 420)
  const frames: Keyframe[] = []
  for (let i = 0; i <= 12; i++) {
    const t = i / 12
    const p = 1 - (1 - t) * (1 - t)
    const x = (1 - p) * (1 - p) * from.x + 2 * (1 - p) * p * ctrl.x + p * p * to.x
    const y = (1 - p) * (1 - p) * from.y + 2 * (1 - p) * p * ctrl.y + p * p * to.y
    const [lx, ly] = unturn(g.fan, x, y)
    frames.push({ translate: `${lx.toFixed(1)}px ${ly.toFixed(1)}px`, rotate: `${(d.lean * (1 - p)).toFixed(2)}deg`, offset: t })
  }
  lift.animate(frames, { duration, easing: 'linear', fill: 'forwards' })
}

export function isNear(
  card: { x: number; y: number },
  slot: { x: number; y: number; w: number; h: number },
  wasNear: boolean,
): boolean {
  const k = wasNear ? NEAR_HOLD : 1
  return Math.abs(card.x - slot.x) <= slot.w * NEAR_ACROSS * k && Math.abs(card.y - slot.y) <= slot.h * NEAR_ALONG * k
}

/** The landing place in the trick (Felt.tsx `DropSlot`): shown faintly as a
 *  playable card is picked up — this is where it would go — and lit, with
 *  "Let go to play", once letting go would put it there. An attribute React
 *  does not manage, so no re-render of the trick can drop it mid-drag. */
function showDrop(state: 'ready' | 'armed' | null): void {
  const trick = document.querySelector<HTMLElement>('.play-felt .trick')
  if (!trick) return
  // Called every frame of a drag. Only a change is written: a write, even of
  // the same value, invalidates the style of the trick and everything in it.
  const now = trick.dataset.drop ?? null
  if (now === state) return
  if (state) trick.dataset.drop = state
  else delete trick.dataset.drop
}

/** Whether a release was a flick up at the table: `samples` are the finger's
 *  last moves (not the lift), `dy` how far it is above where it was picked up
 *  (negative is up), `liftedAt` when the finger left the glass. */
export function isFlick(dy: number, samples: readonly Sample[], liftedAt: number): boolean {
  if (dy > -FLICK_MIN_RISE) return false
  // Held still before letting go is setting it down, not flicking it.
  const last = samples[samples.length - 1]
  if (!last || liftedAt - last.t > PAUSED_MS) return false
  const { fastestUp, atRelease, sideways } = rise(samples)
  return fastestUp >= FLICK_SPEED && atRelease >= STILL_RISING && sideways < fastestUp * 1.5
}

/** How fast the card was rising, at its fastest in the window and as the
 *  finger lifted, in px/ms upwards (so a card falling back reads negative),
 *  and how fast it was going sideways at that fastest moment. */
function rise(samples: readonly Sample[]): { fastestUp: number; atRelease: number; sideways: number } {
  let fastestUp = 0
  let atRelease = 0
  let sideways = 0
  for (let i = samples.length - 1; i > 0; i--) {
    const b = samples[i]
    let j = i - 1
    while (j > 0 && b.t - samples[j].t < SPEED_SPAN_MS) j--
    const a = samples[j]
    const dt = Math.max(b.t - a.t, 1)
    const up = (a.y - b.y) / dt
    if (i === samples.length - 1) atRelease = up
    if (up > fastestUp) {
      fastestUp = up
      sideways = Math.abs(b.x - a.x) / dt
    }
  }
  return { fastestUp, atRelease, sideways }
}

function trim(samples: Sample[]): void {
  const last = samples[samples.length - 1]
  while (samples.length > 2 && last.t - samples[0].t > VELOCITY_MS) samples.shift()
}

function velocity(samples: readonly Sample[]): { x: number; y: number } {
  if (samples.length < 2) return { x: 0, y: 0 }
  const a = samples[0]
  const b = samples[samples.length - 1]
  const dt = Math.max(b.t - a.t, 1)
  return { x: (b.x - a.x) / dt, y: (b.y - a.y) / dt }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v))
}
